import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import VanillaContextMenu from 'vanilla-context-menu';
import { DateTime } from 'luxon';
import { useLocationHoursContext } from '../location-hours.context';
import { useTranslation } from 'react-i18next';
import SideCalendar from './side-calendar/side-calendar.component';
import { Paper, Stack, useTheme } from '@mui/material';
import CustomHeaderToolbar from './custom-header-toolbar/custom-header-toolbar.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import SetTemporaryHoursDrawer from 'components/drawers/location-hours-drawers/set-temporary-hours-drawer/set-temporary-hours-drawer.component';
import locationHoursEvent from '../location-hours-event/location-hours-event.component';
import '../index.css';
import {
  LocationHoursOrderType,
  convertToSetHoursFormData,
  filterAllDayEventsSelectedRange,
  isPastDate,
} from '../location-hours.utils';
import SetClosedDatesDrawer from 'components/drawers/location-hours-drawers/set-closed-dates-drawer/set-closed-dates-drawer.component';
import StandardHoursList from './standard-hours-list/standard-hours-list.component';

export const TemporaryHoursCalendar = () => {
  const { t } = useTranslation();
  const {
    temporaryHours: {
      temporaryHoursRef,
      temporaryEvents,
      eventsSet,
      addDummyEvent: addPickupDummyEvent,
      changeToWeekOfDateView,
    },
    closedDates: { addDummyEvent: addPickupClosedDummyEvent },
  } = useLocationHoursContext();

  const handleEventOverlap = (stillEvent, movingEvent) =>
    ['background', 'none'].includes(stillEvent.display) ||
    stillEvent.extendedProps.orderType !== movingEvent.extendedProps.orderType;

  const handleEventDropResize = info => {
    const { event, oldEvent } = info;

    if (
      oldEvent.allDay !== event.allDay ||
      isPastDate(oldEvent.start) ||
      isPastDate(event.start)
    ) {
      info.revert();
    }
  };

  const setTemporaryHoursDrawer = useUtilityDrawer();
  const setClosedDatesDrawer = useUtilityDrawer();

  const theme = useTheme();

  const root = document.documentElement;
  root.style.setProperty(
    '--fc-page-bg-color',
    theme.palette.background.default,
  );

  root.style.setProperty(
    '--context-menu-item-text-color',
    theme.palette.text.primary,
  );

  root.style.setProperty(
    '--context-menu-item-hover-bg-color',
    theme.palette.action.hover,
  );

  return (
    <Stack
      direction="row"
      gap={2}
      data-testid="TemporaryHours.Calendar"
      sx={{
        '& .fc .fc-timegrid-col-bg .fc-bg-event': {
          mr: 1,
        },
      }}
    >
      <Stack>
        <SideCalendar />
        <StandardHoursList />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack width="100%" gap={2}>
          <CustomHeaderToolbar />
          <FullCalendar
            plugins={[
              listPlugin,
              timeGridPlugin,
              dayGridPlugin,
              multiMonthPlugin,
              interactionPlugin,
            ]}
            now={DateTime.local().toJSDate()}
            initialView="timeGridWeek" // Set initial view to Week
            initialEvents={temporaryEvents}
            eventOrder="-title" // otherwise events can swap columns and we don't want that
            eventsSet={eventsSet}
            allDayText={t('LocationHours.temporaryHours.calendar.allDayText')}
            editable
            eventResizableFromStart
            ref={temporaryHoursRef}
            eventOverlap={handleEventOverlap}
            headerToolbar={false}
            // dayHeaderFormat={{ weekday: 'short' }}
            dateClick={dateSelect => {
              const selectedDate = DateTime.fromJSDate(dateSelect.date).startOf(
                'day',
              );

              const allDayEvents = filterAllDayEventsSelectedRange(
                temporaryEvents,
                selectedDate,
                selectedDate.endOf('day'),
              );

              if (dateSelect.view.type !== 'timeGridWeek') {
                changeToWeekOfDateView(
                  temporaryHoursRef.current?.getApi(),
                  DateTime.fromJSDate(dateSelect.date),
                );
              } else {
                if (!isPastDate(dateSelect.date) && allDayEvents.length < 2) {
                  if (dateSelect.allDay) {
                    const start = selectedDate;
                    const end = selectedDate.endOf('day');
                    const orderTypes: LocationHoursOrderType[] = [
                      'PICKUP_CLOSED',
                      'DELIVERY_CLOSED',
                    ];

                    const orderType = orderTypes.filter(
                      orderType =>
                        !allDayEvents.some(
                          event => event.extendedProps.orderType === orderType,
                        ),
                    )[0];

                    const dummyEvent = addPickupClosedDummyEvent(
                      start,
                      end,
                      orderType,
                    );

                    setClosedDatesDrawer.toggleWithData(
                      convertToSetHoursFormData({
                        title: dummyEvent.title,
                        start,
                        end,
                        allDayEvents: [...allDayEvents, dummyEvent],
                      }),
                    );
                  } else {
                    const start = DateTime.fromJSDate(dateSelect.date);
                    const end = start.plus({ minutes: 30 });

                    const orderTypes: LocationHoursOrderType[] = [
                      'PICKUP',
                      'DELIVERY',
                    ];

                    const orderType = orderTypes.filter(orderType => {
                      if (orderType === 'PICKUP') {
                        return !allDayEvents.some(
                          event =>
                            event.extendedProps.orderType === 'PICKUP_CLOSED',
                        );
                      }
                      return !allDayEvents.some(
                        event =>
                          event.extendedProps.orderType === 'DELIVERY_CLOSED',
                      );
                    })[0];

                    const dummyEvent = addPickupDummyEvent(
                      start,
                      end,
                      orderType,
                    );

                    setTemporaryHoursDrawer.toggleWithData(
                      convertToSetHoursFormData({
                        title: dummyEvent.title,
                        start,
                        end,
                        allDayEvents,
                      }),
                    );
                  }
                }
              }
            }}
            eventClick={({ event }) => {
              // Workaround: Hide elements with the class .fc-popover.fc-more-popover
              // https://github.com/fullcalendar/fullcalendar/issues/6993
              const popovers = document.querySelectorAll(
                '.fc-popover.fc-more-popover',
              );
              popovers.forEach(popover => {
                (popover as HTMLElement).style.display = 'none';
              });

              if (
                !['background', 'none'].includes(event.display) &&
                !isPastDate(event.start)
              ) {
                const start = DateTime.fromJSDate(event.start).startOf('day');
                const end = DateTime.fromJSDate(event.start).endOf('day');

                const allDayEvents = filterAllDayEventsSelectedRange(
                  temporaryEvents,
                  start,
                  end,
                );

                if (event.allDay) {
                  setClosedDatesDrawer.toggleWithData(
                    convertToSetHoursFormData({
                      id: event.id,
                      title: event.title,
                      start,
                      end,
                      allDayEvents: [...allDayEvents, event],
                    }),
                  );
                } else {
                  setTemporaryHoursDrawer.toggleWithData(
                    convertToSetHoursFormData({
                      id: event.id,
                      title: event.title,
                      start: DateTime.fromJSDate(event.start),
                      end: DateTime.fromJSDate(event.end),
                      allDayEvents,
                    }),
                  );
                }
              }
            }}
            scrollTime="00:00:00"
            eventDrop={handleEventDropResize}
            eventResize={handleEventDropResize}
            eventDidMount={({ event, el }) => {
              if (
                !['background', 'none'].includes(event.display) &&
                !isPastDate(event.start)
              ) {
                new VanillaContextMenu({
                  scope: el,
                  normalizePosition: false,
                  transitionDuration: 0,
                  customClass: 'context-menu-custom',
                  customThemeClass: 'context-menu-item-custom',
                  menuItems: [
                    {
                      label: t(
                        'LocationHours.temporaryHours.calendar.event.menuItems.edit',
                      ),
                      callback: () => {
                        const start = DateTime.fromJSDate(event.start).startOf(
                          'day',
                        );
                        const end = DateTime.fromJSDate(event.start).endOf(
                          'day',
                        );

                        const allDayEvents = filterAllDayEventsSelectedRange(
                          temporaryEvents,
                          start,
                          end,
                        );

                        if (event.allDay) {
                          setClosedDatesDrawer.toggleWithData(
                            convertToSetHoursFormData({
                              id: event.id,
                              title: event.title,
                              start,
                              end,
                              allDayEvents: [...allDayEvents, event],
                            }),
                          );
                        } else {
                          setTemporaryHoursDrawer.toggleWithData(
                            convertToSetHoursFormData({
                              id: event.id,
                              title: event.title,
                              start: DateTime.fromJSDate(event.start),
                              end: DateTime.fromJSDate(event.end),
                              allDayEvents,
                            }),
                          );
                        }
                      },
                    },
                    'hr',
                    {
                      label: t(
                        'LocationHours.temporaryHours.calendar.event.menuItems.delete',
                      ),
                      callback: () => {
                        event.remove();
                      },
                    },
                  ],
                });
              }
            }}
            eventContent={locationHoursEvent}
            height="auto"
            dayCellDidMount={cell => {
              if (isPastDate(cell.date)) {
                cell.el.classList.add('fc-past');
              }
            }}
          />
          <SetTemporaryHoursDrawer {...setTemporaryHoursDrawer} />
          <SetClosedDatesDrawer {...setClosedDatesDrawer} />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default TemporaryHoursCalendar;
